import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { useCatalog } from "@/context/CatalogContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { TESTIDS } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SearchOverlay from "@/components/SearchOverlay";

/**
 * Header — Iteration 8 · elephantrooms-style horizontal nav.
 * Left: logo. Center (desktop): NOVIDADES · COLEÇÃO (dropdown) · TOTAL LOOK.
 * Right: search + account + cart. Mobile keeps burger drawer on the left.
 */
const Header = ({ onOpenAuth }) => {
  const { t } = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [colOpen, setColOpen] = useState(false);
  const { count, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const { availableCategories } = useCatalog();
  const { journalCount } = useSiteContent();
  const location = useLocation();
  const navigate = useNavigate();
  const colTimer = useRef(null);

  useEffect(() => { setMenuOpen(false); setColOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const collectionCategories = availableCategories;

  const openCol = () => {
    if (colTimer.current) clearTimeout(colTimer.current);
    setColOpen(true);
  };
  const closeCol = () => {
    // small delay so the mouse can travel between trigger and panel
    // without the dropdown vanishing mid-hop
    if (colTimer.current) clearTimeout(colTimer.current);
    colTimer.current = setTimeout(() => setColOpen(false), 120);
  };

  // Mobile drawer link groups (all secondary/footer navigation)
  const MOBILE_PRIMARY = [
    { label: t("nav.novidades"), to: "/collection/new-in" },
  ];
  const SECONDARY_LINKS = [
    { label: t("nav.about"), to: "/about" },
    ...(journalCount > 0 ? [{ label: t("nav.journal"), to: "/journal" }] : []),
  ];
  const FOOTER_LINKS = [
    { label: t("nav.contact"), to: "/help/contact" },
    { label: t("nav.faq"), to: "/help/faq" },
    { label: t("nav.shipping"), to: "/help/shipping" },
    { label: t("nav.returns"), to: "/help/returns" },
  ];

  return (
    <>
      <div
        className="gl-topbar fixed left-0 right-0 z-50"
        style={{ top: "calc(var(--gl-announce-h, 0px) + var(--gl-topbar-shift, 0px))" }}
      >
        {/* ORANGE BAR */}
        <header className="bg-[var(--gl-orange)] text-[var(--gl-orange-text)] shadow-[0_2px_16px_-8px_rgba(0,0,0,0.25)]">
          <div className="relative flex items-center px-3 md:px-8 h-16 md:h-20 gap-2">
            {/* Left cluster — burger (mobile only) + logo */}
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <button
                data-testid={TESTIDS.headerMenuBtn}
                onClick={() => setMenuOpen(true)}
                aria-label={t("header.openMenu")}
                className="md:hidden p-2 rounded-full hover:bg-black/10 active:scale-95 transition flex flex-col gap-[4px] items-center justify-center"
              >
                <span className="block w-6 h-[2px] bg-current rounded"></span>
                <span className="block w-6 h-[2px] bg-current rounded"></span>
                <span className="block w-6 h-[2px] bg-current rounded"></span>
              </button>
              <Link
                to="/"
                data-testid={TESTIDS.headerLogo}
                className="leading-none flex flex-col text-center"
              >
                <span className="font-serif-display text-2xl md:text-3xl tracking-[0.14em] font-medium">GOYA</span>
                <span className="font-badge text-[0.6rem] md:text-[0.65rem] tracking-[0.5em] opacity-90 mt-1">— LISBOA —</span>
              </Link>
            </div>

            {/* Center — desktop nav */}
            <nav
              data-testid="header-desktop-nav"
              className="hidden md:flex items-center justify-center gap-10 flex-1"
            >
              <Link
                to="/collection/new-in"
                data-testid="nav-novidades"
                className="font-badge text-[12px] tracking-[0.24em] uppercase hover:opacity-70 transition"
              >
                {t("nav.novidades")}
              </Link>

              <div
                className="relative"
                onMouseEnter={openCol}
                onMouseLeave={closeCol}
              >
                <Link
                  to="/collection/all"
                  data-testid="nav-collection"
                  className="font-badge text-[12px] tracking-[0.24em] uppercase hover:opacity-70 transition flex items-center gap-1.5"
                  aria-haspopup="true"
                  aria-expanded={colOpen}
                >
                  {t("nav.collection")}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6"/></svg>
                </Link>
                {colOpen && collectionCategories.length > 0 && (
                  <div
                    data-testid="nav-collection-dropdown"
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                    onMouseEnter={openCol}
                    onMouseLeave={closeCol}
                  >
                    <div className="bg-white text-[#241C18] rounded-lg shadow-[0_12px_32px_-12px_rgba(0,0,0,0.35)] py-3 min-w-[220px] border border-[#F1DCC5]">
                      {collectionCategories.map((c) => (
                        <Link
                          key={c.slug}
                          to={c.to}
                          data-testid={`nav-collection-item-${c.slug}`}
                          className="block px-5 py-2 font-badge text-[11px] tracking-[0.2em] uppercase hover:bg-[#FFE9D6] hover:text-[var(--gl-orange-hover)] transition"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </nav>

            {/* Right cluster */}
            <div className="flex items-center gap-1 md:gap-2 shrink-0 ml-auto md:ml-0">
              <button
                data-testid={TESTIDS.headerSearchBtn}
                onClick={() => setSearchOpen(true)}
                aria-label={t("header.openSearch")}
                className="p-2 rounded-full hover:bg-black/10 active:scale-95 transition"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
              </button>
              <button
                data-testid={TESTIDS.headerAccountBtn}
                onClick={() => user ? navigate("/account") : onOpenAuth?.()}
                className="p-2 rounded-full hover:bg-black/10 active:scale-95 transition"
                aria-label={t("header.account")}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>
              </button>
              <button
                data-testid={TESTIDS.headerCartBtn}
                onClick={() => setDrawerOpen(true)}
                className="p-2 rounded-full hover:bg-black/10 active:scale-95 transition relative"
                aria-label={t("header.openCart")}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                {count > 0 && (
                  <span className="absolute -top-0 -right-0 bg-[#241C18] text-[var(--gl-orange)] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
                )}
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* MOBILE MENU DRAWER (slides in from the left) */}
      <div
        className={`fixed inset-0 z-[60] transition ${menuOpen ? "pointer-events-auto" : "pointer-events-none"} md:hidden`}
        aria-hidden={!menuOpen}
      >
        <div
          onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-[#2C2C2C] transition-opacity duration-300 ${menuOpen ? "opacity-40" : "opacity-0"}`}
        />
        <aside
          data-testid={TESTIDS.mobileMenu}
          className={`drawer-panel absolute top-0 left-0 h-full w-[86vw] max-w-[420px] flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#E3DFD7]">
            <div className="font-serif-display text-xl tracking-[0.1em]">GOYA LISBOA</div>
            <button
              data-testid={TESTIDS.mobileMenuClose}
              onClick={() => setMenuOpen(false)}
              className="text-2xl leading-none text-[#6B6862] hover:text-[#2C2C2C]"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6">
            {/* Primary — Novidades + Total Look */}
            {MOBILE_PRIMARY.length > 0 && (
              <div className="px-6">
                <nav className="flex flex-col">
                  {MOBILE_PRIMARY.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="font-badge text-lg tracking-[0.06em] text-[#241C18] py-3 border-b border-[#F1DCC5] hover:text-[var(--gl-orange-hover)] transition"
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>
              </div>
            )}

            {/* Coleção — subcategories */}
            {collectionCategories.length > 0 && (
              <div className="px-6 mt-6">
                <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#6B6862] mb-3">{t("nav.collection")}</div>
                <nav className="flex flex-col">
                  <Link
                    to="/collection/all"
                    className="font-badge text-base tracking-[0.06em] text-[#241C18] py-2.5 border-b border-[#F1DCC5] hover:text-[var(--gl-orange-hover)] transition"
                  >
                    {t("nav.viewAll")}
                  </Link>
                  {collectionCategories.map((c) => (
                    <Link
                      key={c.slug}
                      to={c.to}
                      className="font-badge text-base tracking-[0.06em] text-[#241C18] py-2.5 border-b border-[#F1DCC5] last:border-b-0 hover:text-[var(--gl-orange-hover)] transition"
                    >
                      {c.label}
                    </Link>
                  ))}
                </nav>
              </div>
            )}

            <div className="px-6 mt-8 pt-6 border-t border-[#E3DFD7]">
              <nav className="flex flex-col">
                {SECONDARY_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="font-sans-clean text-base text-[#2C2C2C] py-2 tracking-wide hover:text-[var(--gl-orange)] transition"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="px-6 mt-8 pt-6 border-t border-[#E3DFD7]">
              <nav className="flex flex-col">
                {FOOTER_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="font-sans-clean text-sm text-[#6B6862] py-1.5 hover:text-[#2C2C2C] transition"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          <div className="p-6 border-t border-[#E3DFD7] space-y-4">
            <LanguageSwitcher />
            {user ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-typewriter text-[10px] tracking-[0.24em] uppercase text-[#6B6862]">{t("header.signedInAs")}</div>
                  <div className="font-serif-display text-lg italic mt-0.5">{user.name}</div>
                </div>
                <button onClick={logout} className="text-xs font-typewriter tracking-[0.22em] uppercase underline">{t("header.logout")}</button>
              </div>
            ) : (
              <button
                onClick={() => { setMenuOpen(false); onOpenAuth?.(); }}
                className="btn-orange w-full"
              >
                {t("header.signinRegister")}
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* SEARCH OVERLAY */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
